/**
 * Component: SearchBar
 * Purpose: フリーテキスト検索入力UI（RQ-001 FR-003）
 * Re-generatable: Yes
 * Dependencies: lucide-react, @/lib/cn
 */

import { Search, X } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/cn';
import { useI18n } from '@/i18n/useI18n';

export interface SearchBarProps {
  /** プレースホルダーテキスト */
  placeholder?: string;
  /** 検索クエリの値 */
  value: string;
  /** 値変更時のコールバック */
  onChange: (value: string) => void;
  /** 追加のCSSクラス */
  className?: string;
}

/**
 * 検索バーコンポーネント
 *
 * 検索アイコン、入力フィールド、クリアボタンを含む検索UI
 * Escapeキーでクリア、デバウンス処理は親コンポーネントで実装
 *
 * @example
 * ```tsx
 * <SearchBar
 *   placeholder="作品を検索..."
 *   value={searchQuery}
 *   onChange={setSearchQuery}
 * />
 * ```
 */
export function SearchBar({
  placeholder,
  value,
  onChange,
  className,
}: SearchBarProps) {
  const { t } = useI18n();
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Escapeキーでクリア
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFocused) {
        onChangeRef.current('');
        inputRef.current?.blur();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFocused]);

  const handleClear = () => {
    onChange('');
    inputRef.current?.focus();
  };

  return (
    <div className={cn('relative w-full', className)}>
      {/* 検索アイコン */}
      <Search
        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        size={18}
        aria-hidden="true"
      />

      {/* 入力フィールド */}
      <input
        ref={inputRef}
        type="search"
        name="search"
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder ?? t('common.searchPlaceholder')}
        aria-label={t('common.search')}
        className={cn(
          'w-full rounded-md border border-input bg-background',
          'pl-10 pr-10 py-2 text-sm',
          'placeholder:text-muted-foreground',
          'focus-visible:outline-none focus-visible:ring-2',
          'focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'transition-colors'
        )}
      />

      {/* クリアボタン（入力がある場合のみ表示） */}
      {value && (
        <button
          type="button"
          onClick={handleClear}
          aria-label={t('common.clearSearch')}
          className={cn(
            'absolute right-3 top-1/2 -translate-y-1/2',
            'text-muted-foreground hover:text-foreground',
            'transition-colors',
            'focus-visible:outline-none focus-visible:ring-2',
            'focus-visible:ring-ring focus-visible:rounded'
          )}
        >
          <X size={18} />
        </button>
      )}
    </div>
  );
}
